import { makeAutoObservable, observable, runInAction } from 'mobx';
import type { DeviceEventDto } from '@emfit/shared-types';
import { STATUS_API } from 'src/config';
import { EmfitAPI } from '../services/BaseAPI';

type State = {
  events: DeviceEventDto[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
};

class EventLogStoreClass {
  private _state: State = observable({
    events: [],
    loading: false,
    error: null,
    hasMore: true,
  });

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get events() {
    return this._state.events;
  }

  get loading() {
    return this._state.loading;
  }

  get error() {
    return this._state.error;
  }

  get hasMore() {
    return this._state.hasMore;
  }

  async fetchEvents(deviceId?: string, count: number = 20, since?: number) {
    this._state.loading = true;
    this._state.error = null;

    try {
      const params = new URLSearchParams({ count: String(count) });
      if (deviceId) params.set('deviceId', deviceId);
      if (since !== undefined) params.set('since', String(since));
      const endpoint = `${STATUS_API}/v1/event?${params.toString()}`;

      const events = await EmfitAPI.get<DeviceEventDto[]>(endpoint);

      runInAction(() => {
        if (!events) return;
        this._state.events = events;
        this._state.hasMore = events.length === count; // If we got exactly 'count' events, there might be more
        this._state.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this._state.error =
          error instanceof Error ? error.message : 'Failed to fetch events';
        this._state.loading = false;
      });
    }
  }

  async loadMore(deviceId?: string, count: number = 20) {
    if (!this.hasMore || this.loading) return;

    this._state.loading = true;
    this._state.error = null;

    try {
      const lastEvent = this._state.events[this._state.events.length - 1];
      if (!lastEvent) {
        runInAction(() => {
          this._state.loading = false;
        });
        return;
      }

      const endpoint = deviceId
        ? `${STATUS_API}/v1/event?count=${count}&deviceId=${deviceId}&since=${lastEvent.id}`
        : `${STATUS_API}/v1/event?count=${count}&since=${lastEvent.id}`;

      const newEvents = await EmfitAPI.get<DeviceEventDto[]>(endpoint);

      runInAction(() => {
        if (!newEvents) return;
        // Filter out duplicates
        const existingIds = new Set(this._state.events.map((e) => e.id));
        const uniqueNewEvents = newEvents.filter((e) => !existingIds.has(e.id));

        this._state.events = [...this._state.events, ...uniqueNewEvents];
        this._state.hasMore = newEvents.length === count;
        this._state.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this._state.error =
          error instanceof Error ? error.message : 'Failed to load more events';
        this._state.loading = false;
      });
    }
  }

  reset() {
    this._state.events = [];
    this._state.loading = false;
    this._state.error = null;
    this._state.hasMore = true;
  }
}

export const EventLogStore = new EventLogStoreClass();
